import { JourneyAncillary, JourneyMapType } from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'

// Types for better type safety
export interface SegmentInfo {
    from: string
    to: string
    key: string
}

export interface AncillaryDataStructure<T> {
    availableSegments: SegmentInfo[]
    segmentWiseAncillaryData: { [key: string]: T }
}

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Creates a segment key from origin-destination info
 */
export const createSegmentKey = (from: string, to: string): string => `${from}-${to}`

/**
 * Creates segment info object
 */
export const createSegmentInfo = (from: string, to: string): SegmentInfo => ({
    from,
    to,
    key: createSegmentKey(from, to)
})

/**
 * Validates if origin-destination info is complete
 */
export const isValidOriginDestination = (originDestinationInfo: any): boolean => {
    const { from, to } = originDestinationInfo || {}
    return Boolean(from && to)
}

/**
 * Generic function to process ancillary data with similar structure
 */
export const processAncillaryData = <T>(
    journeyAncillaries: any[],
    extractorFn: (segmentAncillary: any) => { data: T[]; originDestinationInfo: any } | null,
    sortFn?: (a: T, b: T) => number
): AncillaryDataStructure<T[]> => {
    const result: AncillaryDataStructure<T[]> = {
        availableSegments: [],
        segmentWiseAncillaryData: {}
    }

    if (isEmpty(journeyAncillaries) || !Array.isArray(journeyAncillaries)) {
        return result
    }

    journeyAncillaries.forEach(journeyAncillary => {
        const { segmentAncillaries } = journeyAncillary || {}

        if (!Array.isArray(segmentAncillaries) || isEmpty(segmentAncillaries)) {
            return
        }

        segmentAncillaries.forEach(segmentAncillary => {
            const extracted = extractorFn(segmentAncillary)

            if (!extracted || isEmpty(extracted.data)) {
                return
            }

            const { data, originDestinationInfo } = extracted

            if (!isValidOriginDestination(originDestinationInfo)) {
                return
            }

            const segmentInfo = createSegmentInfo(originDestinationInfo.from, originDestinationInfo.to)

            result.availableSegments.push(segmentInfo)

            // Apply sorting if provided
            const processedData = sortFn ? [...data].sort(sortFn) : data
            result.segmentWiseAncillaryData[segmentInfo.key] = processedData
        })
    })

    return result
}

export const getJourneyMap = (journeyAncillaries: JourneyAncillary[]): JourneyMapType => {
    if (!Array.isArray(journeyAncillaries) || isEmpty(journeyAncillaries)) {
        return []
    }
    return journeyAncillaries.map(journeyAncillary => {
        const { baggageAncillary, segmentAncillaries, originDestinationInfo } = journeyAncillary || {}
        const { baggageDetails = [] } = baggageAncillary || {}
        return {
            baggageAncillary: {
                baggageDetails:
                    baggageDetails?.map(baggageDetail => {
                        const { originDestinationInfo } = baggageDetail || {}
                        return {
                            originDestinationInfo
                        }
                    }) || []
            },
            segmentAncillaries:
                segmentAncillaries?.map(segmentAncillary => {
                    const { originDestinationInfo } = segmentAncillary || {}
                    return {
                        originDestinationInfo
                    }
                }) || [],
            originDestinationInfo
        }
    })
}
